import type { PixelRect } from "@/home/sheet-crop";
import { cropImageToDataUrl } from "@/home/sheet-crop";

export type AutoSplitOptions = {
  minArea: number;
  padding: number;
  minAlpha: number;
  removeWhiteBg?: boolean;
  removeBlackBg?: boolean;
};

export type DetectedBlob = {
  id: string;
  sx: number;
  sy: number;
  sw: number;
  sh: number;
  area: number;
  previewDataUrl: string;
};

const MAX_ANALYSIS_EDGE = 1280;
const MAX_BLOBS = 120;

export async function detectImageBlobs(
  image: HTMLImageElement,
  options: AutoSplitOptions,
): Promise<DetectedBlob[]> {
  const scale =
    Math.max(image.naturalWidth, image.naturalHeight) > MAX_ANALYSIS_EDGE
      ? MAX_ANALYSIS_EDGE / Math.max(image.naturalWidth, image.naturalHeight)
      : 1;

  const analysisW = Math.max(1, Math.round(image.naturalWidth * scale));
  const analysisH = Math.max(1, Math.round(image.naturalHeight * scale));

  const canvas = document.createElement("canvas");
  canvas.width = analysisW;
  canvas.height = analysisH;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) throw new Error("วิเคราะห์ภาพไม่สำเร็จ");

  ctx.drawImage(image, 0, 0, analysisW, analysisH);
  const data = ctx.getImageData(0, 0, analysisW, analysisH);
  const mask = buildForegroundMask(data, options.minAlpha);
  cleanMaskInPlace(mask, analysisW, analysisH);

  const components = findConnectedComponents(mask, analysisW, analysisH, options.minArea);
  components.sort((a, b) => a.cy - b.cy || a.cx - b.cx);

  const inv = 1 / scale;
  const blobs: DetectedBlob[] = [];

  for (let i = 0; i < Math.min(components.length, MAX_BLOBS); i += 1) {
    const c = components[i];
    const sx = Math.max(0, Math.floor(c.minX * inv));
    const sy = Math.max(0, Math.floor(c.minY * inv));
    const ex = Math.min(image.naturalWidth, Math.ceil((c.maxX + 1) * inv));
    const ey = Math.min(image.naturalHeight, Math.ceil((c.maxY + 1) * inv));
    const pad = options.padding;
    const x1 = Math.max(0, sx - pad);
    const y1 = Math.max(0, sy - pad);
    const x2 = Math.min(image.naturalWidth, ex + pad);
    const y2 = Math.min(image.naturalHeight, ey + pad);
    const rect: PixelRect = {
      sx: x1,
      sy: y1,
      sw: x2 - x1,
      sh: y2 - y1,
    };
    if (rect.sw < 1 || rect.sh < 1) continue;

    blobs.push({
      id: `blob-${i + 1}`,
      sx: rect.sx,
      sy: rect.sy,
      sw: rect.sw,
      sh: rect.sh,
      area: Math.round(c.area / (scale * scale)),
      previewDataUrl: cropImageToDataUrl(image, rect, {
        maxEdge: 96,
        removeWhiteBg: options.removeWhiteBg,
        removeBlackBg: options.removeBlackBg,
      }),
    });
  }

  return blobs;
}

type ComponentBox = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  area: number;
  cx: number;
  cy: number;
};

function buildForegroundMask(data: ImageData, minAlpha: number): Uint8Array {
  const { width, height, data: px } = data;
  const mask = new Uint8Array(width * height);
  let hasTransparency = false;

  for (let i = 3; i < px.length; i += 4) {
    if (px[i] < 250) {
      hasTransparency = true;
      break;
    }
  }

  if (hasTransparency) {
    for (let i = 0, j = 0; i < px.length; i += 4, j += 1) {
      mask[j] = px[i + 3] >= minAlpha ? 1 : 0;
    }
    return mask;
  }

  const bg = estimateBackgroundColor(px, width, height);
  const threshold = 28;
  for (let i = 0, j = 0; i < px.length; i += 4, j += 1) {
    const dr = Math.abs(px[i] - bg.r);
    const dg = Math.abs(px[i + 1] - bg.g);
    const db = Math.abs(px[i + 2] - bg.b);
    mask[j] = dr + dg + db > threshold ? 1 : 0;
  }
  return mask;
}

function estimateBackgroundColor(
  px: Uint8ClampedArray,
  width: number,
  height: number,
): { r: number; g: number; b: number } {
  let r = 0;
  let g = 0;
  let b = 0;
  let n = 0;
  const sample = (x: number, y: number) => {
    const i = (y * width + x) * 4;
    r += px[i];
    g += px[i + 1];
    b += px[i + 2];
    n += 1;
  };
  for (let x = 0; x < width; x += 1) {
    sample(x, 0);
    sample(x, height - 1);
  }
  for (let y = 1; y < height - 1; y += 1) {
    sample(0, y);
    sample(width - 1, y);
  }
  return { r: r / n, g: g / n, b: b / n };
}

function cleanMaskInPlace(mask: Uint8Array, width: number, height: number): void {
  const temp = new Uint8Array(mask.length);
  const idx = (x: number, y: number) => y * width + x;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (!mask[idx(x, y)]) continue;
      let on = 0;
      for (let dy = -1; dy <= 1; dy += 1) {
        for (let dx = -1; dx <= 1; dx += 1) {
          const nx = x + dx;
          const ny = y + dy;
          if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
          if (mask[idx(nx, ny)]) on += 1;
        }
      }
      temp[idx(x, y)] = on >= 5 ? 1 : 0;
    }
  }
  mask.set(temp);

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (!mask[idx(x, y)]) continue;
      temp[idx(x, y)] = 1;
      for (let dy = -1; dy <= 1; dy += 1) {
        for (let dx = -1; dx <= 1; dx += 1) {
          const nx = x + dx;
          const ny = y + dy;
          if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
          if (mask[idx(nx, ny)]) temp[idx(nx, ny)] = 1;
        }
      }
    }
  }
  mask.set(temp);
}

class UnionFind {
  private parent: Int32Array;

  constructor(size: number) {
    this.parent = new Int32Array(size);
    for (let i = 0; i < size; i += 1) this.parent[i] = i;
  }

  find(a: number): number {
    let x = a;
    while (this.parent[x] !== x) {
      this.parent[x] = this.parent[this.parent[x]];
      x = this.parent[x];
    }
    return x;
  }

  union(a: number, b: number): void {
    const ra = this.find(a);
    const rb = this.find(b);
    if (ra !== rb) this.parent[rb] = ra;
  }
}

function findConnectedComponents(
  mask: Uint8Array,
  width: number,
  height: number,
  minArea: number,
): ComponentBox[] {
  const uf = new UnionFind(mask.length);
  const idx = (x: number, y: number) => y * width + x;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (!mask[idx(x, y)]) continue;
      if (x > 0 && mask[idx(x - 1, y)]) uf.union(idx(x, y), idx(x - 1, y));
      if (y > 0 && mask[idx(x, y - 1)]) uf.union(idx(x, y), idx(x, y - 1));
      if (x > 0 && y > 0 && mask[idx(x - 1, y - 1)]) uf.union(idx(x, y), idx(x - 1, y - 1));
      if (x < width - 1 && y > 0 && mask[idx(x + 1, y - 1)]) {
        uf.union(idx(x, y), idx(x + 1, y - 1));
      }
    }
  }

  const stats = new Map<
    number,
    { minX: number; minY: number; maxX: number; maxY: number; area: number; sumX: number; sumY: number }
  >();

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const i = idx(x, y);
      if (!mask[i]) continue;
      const root = uf.find(i);
      let s = stats.get(root);
      if (!s) {
        s = { minX: x, minY: y, maxX: x, maxY: y, area: 0, sumX: 0, sumY: 0 };
        stats.set(root, s);
      }
      s.area += 1;
      s.sumX += x;
      s.sumY += y;
      if (x < s.minX) s.minX = x;
      if (y < s.minY) s.minY = y;
      if (x > s.maxX) s.maxX = x;
      if (y > s.maxY) s.maxY = y;
    }
  }

  const out: ComponentBox[] = [];
  for (const s of stats.values()) {
    if (s.area < minArea) continue;
    out.push({
      minX: s.minX,
      minY: s.minY,
      maxX: s.maxX,
      maxY: s.maxY,
      area: s.area,
      cx: s.sumX / s.area,
      cy: s.sumY / s.area,
    });
  }
  return out;
}

export function defaultRoomCellsFromBlob(
  blob: DetectedBlob,
  useAutoSize: boolean,
  manualW: number,
  manualH: number,
): { cellsW: number; cellsH: number } {
  if (!useAutoSize) {
    return { cellsW: manualW, cellsH: manualH };
  }
  const ref = 48;
  return {
    cellsW: Math.min(4, Math.max(1, Math.ceil(blob.sw / ref))),
    cellsH: Math.min(4, Math.max(1, Math.ceil(blob.sh / ref))),
  };
}
